import * as React from 'react';
import { useGetPublicBrand, type PublicBrand } from '@cdf/api-client-react';

import { useTheme } from '@/components/theme/theme-provider';
import { BrowserIdentityProvider, type BrowserIdentity } from '@/lib/browser-identity';
import { readableForeground, skinFontStack, skinTheme, skinTokens } from '@/lib/customer-skin';
import { usePhoneViewport, useCustomerSkin } from './customer-brand-provider';

/**
 * Public visitor pages carry a tenant brand only when that organisation turns
 * on the `publicPages` switch; the browser tab follows `browserIdentity`.
 * The organisation comes from `?org=<address>` and is remembered for the tab
 * so moving between public pages keeps it.
 */
const ORG_PARAM = 'org';
const STORAGE_KEY = 'cdf.publicBrand.org';
const SLUG_PATTERN = /^[A-Za-z0-9][A-Za-z0-9-]{0,127}$/;

function validSlug(value: string | null | undefined): string | null {
  const slug = value?.trim() ?? '';
  return SLUG_PATTERN.test(slug) ? slug : null;
}

function sessionStore(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Organisation address for public pages, from the query string or this tab's last choice. */
export function publicBrandSlug(search: string): string | null {
  const store = sessionStore();
  const fromQuery = validSlug(new URLSearchParams(search).get(ORG_PARAM));
  if (fromQuery) {
    store?.setItem(STORAGE_KEY, fromQuery);
    return fromQuery;
  }
  return validSlug(store?.getItem(STORAGE_KEY));
}

const PublicBrandContext = React.createContext<PublicBrand | null>(null);

/** The resolved public brand, or null when the shared app identity applies. */
export function usePublicBrand(): PublicBrand | null {
  return React.useContext(PublicBrandContext);
}

/** True when public pages should show the organisation's colours, logo and typography. */
export function publicPagesBranded(brand: PublicBrand | null): brand is PublicBrand & { skin: NonNullable<PublicBrand['skin']> } {
  return Boolean(brand?.publicPages && brand.skin);
}

/** Writes the public skin to the document root and removes it on cleanup. */
function PublicSkinTokens({ skin }: { skin: NonNullable<PublicBrand['skin']> }) {
  const { resolvedTheme } = useTheme();
  const viewport = usePhoneViewport();
  React.useLayoutEffect(() => {
    const root = document.documentElement;
    const themeValues = skinTheme(skin, resolvedTheme);
    const headerBackground = themeValues.headerBackground ?? null;
    const headerForeground = themeValues.headerForeground ?? (headerBackground ? readableForeground(headerBackground) : null);
    const tokens = skinTokens({
      skin, theme: resolvedTheme, themeValues, palette: skin.palette ?? {},
      header: headerBackground && headerForeground ? { background: headerBackground, foreground: headerForeground } : null,
      fontFamily: skinFontStack(skin.typography), surface: 'public', viewport
    });
    const names = Object.keys(tokens);
    for (const [name, token] of Object.entries(tokens)) root.style.setProperty(name, token);
    const surfaces = Boolean(skin.surfaces && Object.values(skin.surfaces).some(item => item != null));
    if (surfaces) root.dataset.customerSurfaces = '';
    return () => {
      for (const name of names) root.style.removeProperty(name);
      if (surfaces) delete root.dataset.customerSurfaces;
    };
  }, [skin, resolvedTheme, viewport]);
  return null;
}

function BrandScopes({ brand, children }: { brand: PublicBrand | null; children: React.ReactNode }) {
  // A signed-in member's own skin always wins over a public organisation brand.
  const memberSkin = useCustomerSkin().skin;
  const applied = memberSkin ? null : brand;
  const name = applied?.name ?? null;
  const iconHref = applied?.logo?.url ?? null;
  const identity = React.useMemo<BrowserIdentity | null>(
    () => applied?.browserIdentity && name ? { name, iconHref } : null,
    [applied?.browserIdentity, name, iconHref]
  );
  return <PublicBrandContext.Provider value={applied}>
    <BrowserIdentityProvider identity={identity}>
      {publicPagesBranded(applied) ? <PublicSkinTokens skin={applied.skin} /> : null}
      {children}
    </BrowserIdentityProvider>
  </PublicBrandContext.Provider>;
}

function PublicBrandFetcher({ slug, children }: { slug: string; children: React.ReactNode }) {
  const query = useGetPublicBrand(slug, { query: { retry: false, staleTime: 60_000, refetchOnWindowFocus: false } });
  return <BrandScopes brand={query.data ?? null}>{children}</BrandScopes>;
}

/** Applies an organisation's public brand around unauthenticated or visitor screens. */
export function PublicBrandBoundary({ slug, children }: { slug: string | null; children: React.ReactNode }) {
  return slug ? <PublicBrandFetcher slug={slug}>{children}</PublicBrandFetcher> : <>{children}</>;
}
